import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Stack, Typography, TypographyVariant } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import cloudUpload from "../../../../assets/cloud-upload.svg";
import { brand, gray, error as red } from "../../../../theme/themePrimitives";
import { useToast } from "../../../../context/ToastContext";
import { getFileNameFromUrl } from "../../../../utils/helpers";
import { PackageType } from "../../../../services/api/types/assignments/assignment";

interface UploadProps {
  value: File | string | null;
  onChange: (file: File | null) => void;
  error?: string;
  label?: string;
  labelVariant?: TypographyVariant;
  mode?: "thumbnail" | "package";
  disabled?: boolean;
  url?: string; // new prop
  packageType?: PackageType;
}

// Limits for uploads.
const MAX_THUMBNAIL_IMAGE_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB
const MAX_THUMBNAIL_IMAGE_SIZE_LABEL = "1 MB";

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB (packages)

const MAX_PDF_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB hard block
const PDF_FILE_SIZE_WARNING_BYTES = 100 * 1024 * 1024; // 100 MB best-practice warning

const MAX_VIDEO_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
const ACCEPTED_VIDEO_MIME_TYPES = ["video/mp4", "video/webm"];

export const UploadFile: React.FC<UploadProps> = ({
  value,
  onChange,
  error,
  label = "Upload thumbnail",
  labelVariant = "h6",
  mode = "thumbnail",
  disabled = false,
  url,
  packageType,
}) => {
  const [displayValue, setDisplayValue] = useState<File | string | null>(value);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const objectUrlRef = useRef<string | null>(null);
  const { showToast } = useToast();

  const isPdfPackage =
    mode === "package" && packageType === PackageType.PdfUpload;
  const isVideoPackage =
    mode === "package" && packageType === PackageType.VideoUpload;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (mode === "thumbnail") {
        if (file.size > MAX_THUMBNAIL_IMAGE_SIZE_BYTES) {
          showToast(
            `Image size must be less than ${MAX_THUMBNAIL_IMAGE_SIZE_LABEL}`,
            "error",
            4000,
          );
          e.target.value = "";
          return;
        }

        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(file);
        objectUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
      } else if (isPdfPackage) {
        if (file.size > MAX_PDF_FILE_SIZE_BYTES) {
          showToast("File size exceeds limit of 2 GB", "error");
          e.target.value = "";
          return;
        }
        if (file.size > PDF_FILE_SIZE_WARNING_BYTES) {
          showToast(
            "This file is larger than the recommended maximum of 100 MB",
            "warning",
          );
        }
      } else if (isVideoPackage) {
        if (file.size > MAX_VIDEO_FILE_SIZE_BYTES) {
          showToast("File size exceeds limit of 2 GB", "error");
          e.target.value = "";
          return;
        }
      } else {
        if (file.size > MAX_FILE_SIZE) {
          showToast("File size exceeds limit of 5 GB", "error");
          return;
        }
      }
      onChange(file);
    }
  };

  const getAccept = () => {
    if (mode === "thumbnail") return "image/*";
    if (isPdfPackage) return "application/pdf";
    if (isVideoPackage) return ACCEPTED_VIDEO_MIME_TYPES.join(",");
    return ".zip";
  };

  const getHelperText = () => {
    if (mode === "thumbnail") return "SVG, PNG, JPG or GIF (max. 800x400px)";
    if (isPdfPackage) return "PDF files only";
    if (isVideoPackage) return "MP4 or WebM files only";
    return "ZIP files only";
  };

  useEffect(() => {
    if (url && !value) {
      setDisplayValue(
        getFileNameFromUrl(url, isPdfPackage || isVideoPackage ? 1 : 2),
      );
    } else if (value) {
      setDisplayValue(value);
    } else {
      setDisplayValue(null);
    }
  }, [url, value, isPdfPackage, isVideoPackage]);

  useEffect(() => {
    if (mode !== "thumbnail") return;

    if (value instanceof File) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(value);
      objectUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
    } else if (url) {
      setPreviewUrl(url);
    } else if (!value) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPreviewUrl(null);
    }
  }, [mode, url, value]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Typography variant={labelVariant} mb={1}>
        {label}
      </Typography>

      <Stack spacing={1}>
        {displayValue && (
          <Box
            sx={{
              border: `1px solid ${gray[100]}`,
              borderRadius: "12px",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
              p: 2,
              minHeight: 120,
            }}
          >
            <Stack
              direction="column"
              alignItems="left"
              spacing={1}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              {mode === "thumbnail" ? (
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "8px",
                    border: `1px solid ${gray[200]}`,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: gray[100],
                    flexShrink: 0,
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={
                        previewUrl.startsWith("blob:")
                          ? previewUrl
                          : `/${previewUrl}`
                      }
                      alt="thumbnail preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <InsertDriveFileIcon sx={{ color: gray[300] }} />
                  )}
                </Box>
              ) : (
                <InsertDriveFileIcon
                  sx={{
                    color: gray[300],
                    flexShrink: 0,
                  }}
                />
              )}

              <Typography
                variant="body2"
                fontWeight="bold"
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {typeof displayValue === "string"
                  ? displayValue
                  : displayValue.name}
              </Typography>
              {typeof displayValue !== "string" && (
                <Typography variant="caption" color="text.secondary">
                  {(displayValue.size / 1024).toFixed(0)} KB
                </Typography>
              )}
            </Stack>
          </Box>
        )}
        <Box
          sx={{
            border: error ? `1px solid ${red[600]}` : `1px solid ${gray[100]}`,
            borderRadius: "12px",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.5 : 1,
            p: 2,
          }}
        >
          <input
            accept={getAccept()}
            type="file"
            style={{ display: "none" }}
            id={`file-upload-input-${mode}`}
            onChange={handleFileChange}
            disabled={disabled}
          />
          <label
            htmlFor={`file-upload-input-${mode}`}
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              pointerEvents: disabled ? "none" : "auto",
              cursor: disabled ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <img
              src={cloudUpload}
              alt="cloud-upload"
              style={{
                border: `1px solid ${gray[200]}`,
                borderRadius: "8px",
                padding: "8px",
              }}
            />
            <Box>
              <Typography
                color={brand[600]}
                sx={{
                  cursor: disabled ? "default" : "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Click to upload
              </Typography>
              <Typography variant="subtitle2">{getHelperText()}</Typography>
            </Box>
          </label>
        </Box>
      </Stack>
      {error && (
        <Typography
          sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}
          fontSize="12px"
          color="error"
        >
          <ErrorOutlineIcon fontSize="small" /> {error}
        </Typography>
      )}
    </Box>
  );
};
